import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  PageHeader,
  ActiveFilters,
  SearchFilterRow,
  SegmentedTabs,
  EntityCard,
  SocialProof,
  NeutralPill,
  chipCls,
  type ProofAvatar,
} from "@/components/kit";
import { SectionHint } from "@/components/SectionHint";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Check, Plus, Users } from "lucide-react";
import { CircleHostsPicker, ChatAccessSegment, type HostProfile } from "@/components/CircleHosts";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CitySelect } from "@/components/CitySelect";
import { EVENT_TOPICS } from "@/lib/event-topics";
import { rankCircles } from "@/lib/circle-rank";
import { cityFilterOptions } from "@/lib/filter-options";
import { TURKEY_CITIES } from "@/lib/people-constants";
import { pluralParticipants } from "@/lib/plural";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CHAT_URL_HINT,
  CHAT_CLOSED_HINT,
  IG_HINT,
  TG_HINT,
  CIRCLE_COLS,
  isCircleHost,
  useCircleCohosts,
  DeleteCircleButton,
  normalizeUrl,
  CHAT_URL_ERROR,
  profileHandleToUrl,
  useRestoreCircle,
  useCircleMembership,
  type CircleRow,
} from "./circles";

export const Route = createFileRoute("/_authenticated/circles/")({
  head: () => ({
    meta: [
      { title: "Сообщества — тематические группы TurkeyDrive" },
      {
        name: "description",
        content: "Сообщества TurkeyDrive: тематические группы по интересам и городам Турции.",
      },
      { property: "og:title", content: "Сообщества — тематические группы TurkeyDrive" },
      {
        property: "og:description",
        content: "Тематические мини-сообщества TurkeyDrive: встречи, запросы и свои люди рядом.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { create?: boolean } => ({
    create:
      search.create === true || search.create === "1" || search.create === "true"
        ? true
        : undefined,
  }),
  component: CirclesList,
});

const EMOJI_CHOICES = ["🌿", "🧘", "👩‍👧", "💼", "🔮", "🎨", "⚽", "🍲", "📚", "🎵", "🐾", "✈️"];

const STATUS_LABEL: Record<string, string> = {
  pending: "На модерации",
  published: "Опубликовано",
  rejected: "Отклонено",
  archived: "Снято с публикации",
};

function useIsAdmin() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", user.id)
        .maybeSingle();
      return Boolean(data?.is_admin);
    },
  });
}

function CirclesList() {
  const { user } = useAuth();
  const uid = user?.id;
  const { create: createFromUrl } = Route.useSearch();
  const [createOpen, setCreateOpen] = useState(!!createFromUrl);
  const navigate = useNavigate();

  // RLS отдаёт опубликованные + круги, где ты хранитель (и все — админу).
  const { data: circles = [], isLoading } = useQuery({
    queryKey: ["circles", uid ?? "anon"],
    queryFn: async () => {
      const query = supabase.from("circles").select(CIRCLE_COLS).eq("is_deleted", false);
      const { data, error } = await (uid ? query : query.eq("status", "published")).order(
        "created_at",
        { ascending: true },
      );
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const { data: cohosts = [] } = useCircleCohosts(circles.map((c) => c.id));

  // Снятые с публикации круги не показываем никому в общем списке —
  // админ управляет ими в разделе модерации ниже.
  const visible = circles.filter(
    (c) => c.status === "published" || (isCircleHost(c, uid, cohosts) && c.status !== "archived"),
  );

  const { data: members = [] } = useQuery({
    queryKey: ["circle-members", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("circle_members").select("circle_id, user_id");
      if (error) throw error;
      return (data ?? []) as { circle_id: string; user_id: string }[];
    },
  });

  const hostIds = Array.from(
    new Set(
      [
        ...visible.map((c) => c.host_user_id),
        ...cohosts.filter((h) => visible.some((c) => c.id === h.circle_id)).map((h) => h.user_id),
      ].filter(Boolean),
    ),
  ) as string[];

  const { data: hosts = [] } = useQuery({
    queryKey: ["circle-hosts", hostIds.join(",")],
    enabled: hostIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url")
        .in("user_id", hostIds);
      if (error) throw error;
      return (data ?? []) as HostProfile[];
    },
  });

  const hostsOf = (c: CircleRow): HostProfile[] =>
    [c.host_user_id, ...cohosts.filter((h) => h.circle_id === c.id).map((h) => h.user_id)]
      .map((id) => hosts.find((h) => h.user_id === id))
      .filter((h): h is HostProfile => !!h);

  const countOf = (id: string) => members.filter((m) => m.circle_id === id).length;
  const isMember = (id: string) =>
    !!uid && members.some((m) => m.circle_id === id && m.user_id === uid);

  const { data: myCity = null } = useQuery({
    queryKey: ["profile-city", uid],
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("city").eq("user_id", uid!).maybeSingle();
      return (data?.city as string | undefined) ?? null;
    },
  });
  const { data: nextEventAt = {} } = useQuery({
    queryKey: ["circle-next-events"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("circle_id, event_date")
        .eq("status", "published")
        .not("circle_id", "is", null)
        .gte("event_date", new Date().toISOString())
        .order("event_date", { ascending: true });
      const map: Record<string, string> = {};
      for (const e of data ?? []) if (e.circle_id && e.event_date && !map[e.circle_id]) map[e.circle_id] = e.event_date;
      return map;
    },
  });
  const memberCount: Record<string, number> = {};
  for (const m of members) memberCount[m.circle_id] = (memberCount[m.circle_id] ?? 0) + 1;

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "mine">("all");
  const [topics, setTopicsF] = useState<string[]>([]);
  const [topicDraft, setTopicDraft] = useState<string[]>([]);
  const [city, setCity] = useState("all");
  const [cityDraft, setCityDraft] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { join } = useCircleMembership();

  const q = search.trim().toLowerCase();
  const preTopic = visible.filter((c) => {
    if (tab === "mine" && !isMember(c.id) && !isCircleHost(c, uid, cohosts)) return false;
    // Сообщество без города — «Вся Турция»: видно при любом выбранном городе.
    if (city !== "all" && c.city && c.city !== city) return false;
    if (q && ![c.name, ...(c.topics ?? [])].join(" ").toLowerCase().includes(q)) return false;
    return true;
  });
  const shown = rankCircles(
    topics.length
      ? preTopic.filter((c) => (c.topics ?? []).some((t) => topics.includes(t)))
      : preTopic,
    { userCity: myCity, nextEventAt, memberCount },
  );
  const topicOptions = [...new Set(visible.flatMap((c) => c.topics ?? []))].sort();
  const cityOptions = [
    ...TURKEY_CITIES,
    ...[
      ...new Set(
        visible
          .map((c) => c.city?.trim())
          .filter(
            (c): c is string =>
              !!c && c !== "Не указан" && !(TURKEY_CITIES as readonly string[]).includes(c),
          ),
      ),
    ].sort((a, b) => a.localeCompare(b, "ru")),
  ];

  const memberIdsShown = [
    ...new Set(
      shown.flatMap((c) =>
        members
          .filter((m) => m.circle_id === c.id)
          .slice(0, 5)
          .map((m) => m.user_id),
      ),
    ),
  ].sort();
  const { data: memberPhotos } = useQuery({
    queryKey: ["circle-member-photos", memberIdsShown.join(",")],
    enabled: memberIdsShown.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url")
        .in("user_id", memberIdsShown);
      return new Map(
        (data ?? []).map((p) => [
          p.user_id as string,
          p as { full_name: string; photo_url: string | null },
        ]),
      );
    },
  });
  const avatarsOf = (id: string): ProofAvatar[] =>
    members
      .filter((m) => m.circle_id === id)
      .slice(0, 5)
      .map((m) => memberPhotos?.get(m.user_id))
      .filter((p): p is { full_name: string; photo_url: string | null } => !!p)
      .map((p) => ({
        photo_url: p.photo_url,
        initial: (p.full_name || "?").slice(0, 1).toUpperCase(),
      }));

  return (
    <div className="mx-auto w-full max-w-md px-4 pb-28 pt-6">
      <SectionHint hint="circles" className="mb-4" />
      <PageHeader
        title="Сообщества"
        action={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="cta-solid inline-flex h-10 items-center rounded-full px-4 text-sm"
          >
            <Plus className="mr-1 h-4 w-4" />
            Создать
          </button>
        }
      />
      <div className="mt-4">
        <SearchFilterRow
          value={search}
          onChange={setSearch}
          placeholder="Поиск сообществ"
          filterCount={(city !== "all" ? 1 : 0) + topics.length}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={(o) => {
            setFiltersOpen(o);
            if (o) {
              setCityDraft(city);
              setTopicDraft(topics);
            }
          }}
          onReset={() => {
            setCity("all");
            setCityDraft("all");
            setTopicsF([]);
            setTopicDraft([]);
            setFiltersOpen(false);
          }}
          onApply={() => {
            setCity(cityDraft);
            setTopicsF(topicDraft);
            setFiltersOpen(false);
          }}
          sections={[
            {
              key: "city",
              title: "Город",
              mode: "single",
              options: cityFilterOptions(cityOptions),
              value: cityDraft,
              onChange: setCityDraft,
            },
            ...(topicOptions.length
              ? [
                  {
                    key: "topics",
                    title: "Темы",
                    mode: "multi" as const,
                    options: topicOptions.map((t) => ({ value: t, label: t })),
                    value: topicDraft,
                    onChange: setTopicDraft,
                  },
                ]
              : []),
          ]}
        />
      </div>
      <div className="mt-3">
        <SegmentedTabs
          value={tab}
          onChange={setTab}
          items={[
            { key: "all", label: "Все" },
            { key: "mine", label: "Мои" },
          ]}
        />
      </div>
      <ActiveFilters
        items={[
          ...(city !== "all" ? [{ key: "city", label: city, onRemove: () => setCity("all") }] : []),
          ...topics.map((t) => ({
            key: `t-${t}`,
            label: t,
            onRemove: () => setTopicsF((prev) => prev.filter((v) => v !== t)),
          })),
        ]}
        onReset={() => {
          setCity("all");
          setTopicsF([]);
        }}
      />

      {isLoading && <p className="mt-4 text-sm text-td-muted">Загружаем сообщества…</p>}

      {!isLoading && shown.length === 0 && (
        <div className="mt-4 rounded-2xl border border-td-line bg-white p-5 text-center">
          <p className="text-sm text-td-muted">
            {visible.length === 0
              ? "Сообществ пока нет — собери первое."
              : "Ничего не нашлось. Попробуй изменить поиск или фильтры."}
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {shown.map((c) => {
          const member = isMember(c.id);
          const mine = isCircleHost(c, uid, cohosts);
          const list = hostsOf(c);
          const count = countOf(c.id);
          const host = list[0];
          return (
            <EntityCard
              key={c.id}
              onOpen={() => void navigate({ to: "/circles/$circleId", params: { circleId: c.id } })}
              image={
                c.logo_url ? (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={c.logo_url}
                    alt={c.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-td-soft text-4xl">
                    {c.cover}
                  </span>
                )
              }
              meta={
                <>
                  <span className="min-w-0 truncate">{c.city ?? "Вся Турция"}</span>
                  {c.is_official && <NeutralPill>Official</NeutralPill>}
                  {mine && c.status !== "published" && (
                    <NeutralPill>{STATUS_LABEL[c.status] ?? c.status}</NeutralPill>
                  )}
                </>
              }
              title={c.name}
              author={
                host ? (
                  <span className="flex min-w-0 items-center gap-1.5">
                    {host.photo_url ? (
                      <img
                        src={host.photo_url}
                        alt=""
                        loading="lazy"
                        className="h-5 w-5 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-td-soft text-[10px] font-semibold text-td-dark">
                        {(host.full_name || "?").slice(0, 1)}
                      </span>
                    )}
                    <span className="min-w-0 truncate">
                      Лидер: {host.full_name}
                      {list.length > 1 ? ` и ещё ${list.length - 1}` : ""}
                    </span>
                  </span>
                ) : undefined
              }
              extra={
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {pluralParticipants(count)}
                </span>
              }
              proof={
                <SocialProof
                  count={count}
                  label={pluralParticipants(count)}
                  avatars={avatarsOf(c.id)}
                />
              }
              action={
                c.status !== "published" || mine ? (
                  <Link
                    to="/circles/$circleId"
                    params={{ circleId: c.id }}
                    className="inline-flex h-11 items-center rounded-full border border-td-teal px-4 text-[13px] font-semibold text-td-teal hover:bg-td-teal/10"
                  >
                    Управлять
                  </Link>
                ) : member ? (
                  <span className="inline-flex h-11 items-center gap-1 rounded-full bg-td-soft px-4 text-[13px] font-semibold text-td-dark">
                    Ты в сообществе <Check className="h-4 w-4" />
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void join(c.id)}
                    className="inline-flex h-11 items-center rounded-full bg-td-teal px-5 text-[13px] font-semibold text-white hover:bg-td-teal/90"
                  >
                    Вступить
                  </button>
                )
              }
            />
          );
        })}
      </div>

      <CreateCircleDialog open={createOpen} onOpenChange={setCreateOpen} />

      <CirclesModeration />
    </div>
  );
}

/* ------------------------------ Create circle ------------------------------ */

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-td-dark">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-td-muted">{hint}</p>}
    </div>
  );
}

function CreateCircleDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [topics, setTopics] = useState<string[]>([]);
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState("🌿");
  const [chatUrl, setChatUrl] = useState("");
  const [chatAccess, setChatAccess] = useState<"open" | "closed">("open");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [telegramUrl, setTelegramUrl] = useState("");
  const [hostLink, setHostLink] = useState("");
  const [hostLinkTouched, setHostLinkTouched] = useState(false);
  const [cohosts, setCohosts] = useState<HostProfile[]>([]);

  const { data: myContacts } = useQuery({
    queryKey: ["my-contacts-for-circle", user?.id],
    enabled: open && !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, social_links, telegram")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (!open || hostLinkTouched || hostLink || !myContacts) return;
    const social = (myContacts.social_links ?? []).find((s) => s?.trim());
    if (social) setHostLink(profileHandleToUrl(social, "instagram"));
    else if (myContacts.telegram?.trim())
      setHostLink(profileHandleToUrl(myContacts.telegram, "telegram"));
  }, [open, myContacts, hostLinkTouched, hostLink]);

  const reset = () => {
    setName("");
    setTopics([]);
    setCity("");
    setDescription("");
    setCover("🌿");
    setChatUrl("");
    setChatAccess("open");
    setInstagramUrl("");
    setTelegramUrl("");
    setHostLink("");
    setHostLinkTouched(false);
    setCohosts([]);
  };

  const create = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Нужно войти");
      if (!name.trim()) throw new Error("Введите название сообщества");
      if (topics.length === 0) throw new Error("Выберите хотя бы одну тему");
      if (topics.length > 3) throw new Error("Можно выбрать не больше трёх тем");
      const host_link = normalizeUrl(
        hostLink,
        "Твоя ссылка на инстаграм или сайт должна начинаться с https:// или http://",
      );
      if (!host_link) throw new Error("Укажи свой инстаграм или сайт для проверки");
      const chat_url = chatAccess === "open" ? normalizeUrl(chatUrl, CHAT_URL_ERROR) : null;
      const instagram_url = normalizeUrl(instagramUrl, "Проверь ссылку на инстаграм сообщества");
      const telegram_url = normalizeUrl(telegramUrl, "Проверь ссылку на телеграм-канал");
      const { data, error } = await supabase
        .from("circles")
        .insert({
          name: name.trim(),
          description: description.trim() || null,
          cover,
          topics,
          city: city.trim() || null,
          host_user_id: user.id,
          host_link,
          instagram_url,
          telegram_url,
          chat_access: chatAccess,
          status: "pending",
        })
        .select("id")
        .single();
      if (error) throw error;
      let linkFailed = false;
      let hostsFailed = false;
      if (chat_url) {
        const { error: linkErr } = await supabase
          .from("circle_chat_links")
          .insert({ circle_id: data.id, url: chat_url });
        if (linkErr) {
          console.error(linkErr);
          linkFailed = true;
        }
      }
      if (cohosts.length) {
        const { error: hErr } = await supabase
          .from("circle_hosts")
          .insert(
            cohosts.map((p) => ({ circle_id: data.id, user_id: p.user_id, added_by: user.id })),
          );
        if (hErr) {
          console.error(hErr);
          hostsFailed = true;
        }
      }
      return { linkFailed, hostsFailed };
    },
    onSuccess: async (res) => {
      if (res.linkFailed) {
        toast.warning(
          "Сообщество создано, но ссылку на чат сохранить не удалось — добавь её в управлении сообществом",
        );
      } else if (res.hostsFailed) {
        toast.warning(
          "Сообщество создано, но со-лидеров добавить не удалось — добавь их в управлении сообществом",
        );
      } else {
        toast.success("Сообщество отправлено на модерацию");
      }
      reset();
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: ["circles"] });
      await qc.invalidateQueries({ queryKey: ["circle-cohosts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-sm overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle>Собери своё сообщество</DialogTitle>
          <DialogDescription>
            Опиши сообщество — мы проверим его и опубликуем. Ты станешь лидером и первым
            участником.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-6">
          <div className="space-y-4">
            <SectionTitle title="О сообществе" />
            <div>
              <Label htmlFor="circle-name">Название</Label>
              <Input
                id="circle-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Бег в Стамбуле"
                maxLength={80}
              />
            </div>

            <div>
              <Label>Обложка</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {EMOJI_CHOICES.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setCover(e)}
                    className={`flex h-9 w-9 items-center justify-center rounded-xl border text-lg ${
                      cover === e ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Темы</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {EVENT_TOPICS.map((t) => {
                  const active = topics.includes(t);
                  const disabled = topics.length >= 3 && !active;
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={disabled}
                      onClick={() =>
                        setTopics((prev) => (active ? prev.filter((x) => x !== t) : [...prev, t]))
                      }
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        active
                          ? "bg-td-teal text-white"
                          : disabled
                            ? "cursor-not-allowed bg-td-soft text-td-muted opacity-60"
                            : "bg-td-teal/10 text-td-teal"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 text-xs text-td-muted">
                Максимум 3 темы — так понятнее, чем занимается сообщество
              </p>
            </div>

            <div>
              <Label>Город</Label>
              <CitySelect
                className="mt-1"
                value={city}
                onChange={setCity}
                allTurkeyLabel="Вся Турция (без города)"
              />
            </div>

            <div>
              <Label htmlFor="circle-desc">Описание / философия сообщества</Label>
              <Textarea
                id="circle-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Для кого, зачем и о чём это сообщество"
                rows={4}
                maxLength={1000}
              />
            </div>

            <div>
              <Label>Лидеры</Label>
              <div className="mt-1">
                <CircleHostsPicker
                  creator={{
                    label: "Ты, создатель",
                    profile: myContacts
                      ? {
                          user_id: myContacts.user_id,
                          full_name: myContacts.full_name,
                          photo_url: myContacts.photo_url,
                        }
                      : null,
                  }}
                  selected={cohosts}
                  onAdd={(p) => setCohosts((prev) => (prev.length >= 3 ? prev : [...prev, p]))}
                  onRemove={(id) => setCohosts((prev) => prev.filter((p) => p.user_id !== id))}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <SectionTitle
              title="Ссылки сообщества"
              hint="Укажи, где сообщество уже живёт. Если чего-то нет, оставь пустым"
            />
            <div>
              <Label htmlFor="circle-instagram">Инстаграм сообщества</Label>
              <Input
                id="circle-instagram"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="https://instagram.com/..."
                maxLength={300}
              />
              <p className="mt-1 text-xs text-td-muted">{IG_HINT}</p>
            </div>

            <div>
              <Label htmlFor="circle-telegram">Телеграм-канал сообщества</Label>
              <Input
                id="circle-telegram"
                value={telegramUrl}
                onChange={(e) => setTelegramUrl(e.target.value)}
                placeholder="https://t.me/..."
                maxLength={300}
              />
              <p className="mt-1 text-xs text-td-muted">{TG_HINT}</p>
            </div>

            <div>
              <Label>Чат сообщества (Telegram или WhatsApp)</Label>
              <ChatAccessSegment value={chatAccess} onChange={setChatAccess} />
              {chatAccess === "open" ? (
                <>
                  <Input
                    id="circle-chat"
                    className="mt-2"
                    value={chatUrl}
                    onChange={(e) => setChatUrl(e.target.value)}
                    placeholder="https://t.me/..."
                    maxLength={300}
                  />
                  <p className="mt-1 text-xs text-td-muted">{CHAT_URL_HINT}</p>
                </>
              ) : (
                <p className="mt-2 text-xs text-td-muted">{CHAT_CLOSED_HINT}</p>
              )}
            </div>
          </div>

          <div className="space-y-2 rounded-2xl bg-td-soft p-3">
            <SectionTitle title="Для проверки командой TurkeyDrive" />
            <div>
              <Label htmlFor="circle-host-link">Твой инстаграм или сайт *</Label>
              <Input
                id="circle-host-link"
                className="bg-white"
                value={hostLink}
                onChange={(e) => {
                  setHostLinkTouched(true);
                  setHostLink(e.target.value);
                }}
                placeholder="https://instagram.com/твой_ник"
                maxLength={300}
              />
              <p className="mt-1 text-xs text-td-muted">
                Личная ссылка создателя. Её видит только модератор, участники не увидят
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            className="cta-solid h-11 w-full rounded-full"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            Отправить на модерацию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------- Admin: moderation ---------------------------- */

function CirclesModeration() {
  const { data: isAdmin } = useIsAdmin();
  const qc = useQueryClient();

  const { data: pending = [] } = useQuery({
    queryKey: ["circles", "pending-moderation"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select(CIRCLE_COLS)
        .eq("is_deleted", false)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const { data: published = [] } = useQuery({
    queryKey: ["circles", "published-admin"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select(CIRCLE_COLS)
        .eq("is_deleted", false)
        .eq("status", "published")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const { data: archived = [] } = useQuery({
    queryKey: ["circles", "archived-admin"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select(CIRCLE_COLS)
        .eq("is_deleted", false)
        .in("status", ["archived", "rejected"])
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const { data: deleted = [] } = useQuery({
    queryKey: ["circles", "deleted-admin"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select(CIRCLE_COLS)
        .eq("is_deleted", true)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const pendingIds = pending.map((c) => c.id);
  const { data: chatLinks = {} } = useQuery({
    queryKey: ["circle-chat-link", "moderation", pendingIds.join(",")],
    enabled: !!isAdmin && pendingIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_chat_links")
        .select("circle_id, url")
        .in("circle_id", pendingIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((r) => [r.circle_id, r.url])) as Record<
        string,
        string
      >;
    },
  });

  const { data: modCohosts = [] } = useCircleCohosts(isAdmin ? pendingIds : []);
  const hostIds = Array.from(
    new Set(
      [...pending.map((c) => c.host_user_id), ...modCohosts.map((h) => h.user_id)].filter(Boolean),
    ),
  ) as string[];
  const { data: modHosts = {} } = useQuery({
    queryKey: ["circles", "moderation-hosts", hostIds.join(",")],
    enabled: !!isAdmin && hostIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, username, photo_url, city, created_at, social_links, telegram")
        .in("user_id", hostIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((p) => [p.user_id, p]));
    },
  });

  const { data: hostLinks = {} } = useQuery({
    queryKey: ["circles", "moderation-host-links", pendingIds.join(",")],
    enabled: !!isAdmin && pendingIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select("id, host_link")
        .in("id", pendingIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((r) => [r.id, r.host_link])) as Record<
        string,
        string | null
      >;
    },
  });

  const restore = useRestoreCircle();

  const moderate = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("circles").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["circles"] });
      qc.invalidateQueries({ queryKey: ["circle-members"] });
      toast.success(
        vars.status === "published"
          ? "Сообщество опубликовано"
          : vars.status === "rejected"
            ? "Сообщество отклонено"
            : "Сообщество снято с публикации",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isAdmin) return null;

  return (
    <section className="mt-6 rounded-2xl border border-td-teal/30 bg-td-soft/50 p-4">
      <h2 className="text-lg font-semibold text-td-dark">
        Модерация сообществ{pending.length ? ` · ${pending.length}` : ""}
      </h2>

      {pending.length === 0 ? (
        <p className="mt-2 text-sm text-td-muted">Нет сообществ на модерации.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {pending.map((c) => (
            <li key={c.id} className="rounded-xl border border-td-teal/20 bg-white p-3">
              <p className="text-sm font-medium text-td-dark break-words">
                {c.cover} {c.name}
              </p>
              <p className="mt-1 text-xs text-td-muted">
                {c.city || "Вся Турция"} · {(c.topics ?? []).join(", ")}
              </p>
              {c.description && (
                <p className="mt-2 text-sm text-td-dark/80 break-words">{c.description}</p>
              )}
              {chatLinks[c.id] && (
                <p className="mt-2 text-xs text-td-muted break-all">
                  💬 Чат:{" "}
                  <a
                    href={chatLinks[c.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-td-teal underline"
                  >
                    {chatLinks[c.id]}
                  </a>
                </p>
              )}
              {hostLinks[c.id] && (
                <p className="mt-2 text-xs text-td-muted break-all">
                  🔍 Для проверки:{" "}
                  <a
                    href={hostLinks[c.id]!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-td-teal underline"
                  >
                    {hostLinks[c.id]}
                  </a>
                </p>
              )}
              {[
                c.host_user_id,
                ...modCohosts.filter((h) => h.circle_id === c.id).map((h) => h.user_id),
              ]
                .filter((id): id is string => !!id)
                .map((id, i) => (
                  <div key={id}>
                    {i > 0 && i === 1 && (
                      <p className="mt-3 text-xs font-semibold text-td-muted">Со-лидеры</p>
                    )}
                    <ModerationHost p={modHosts[id]} userId={id} />
                  </div>
                ))}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  className="bg-td-teal hover:bg-td-teal/90"
                  disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ id: c.id, status: "published" })}
                >
                  Одобрить
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ id: c.id, status: "rejected" })}
                >
                  Отклонить
                </Button>
                <DeleteCircleButton circleId={c.id} circleName={c.name} label="Удалить" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {published.length > 0 && (
        <>
          <h3 className="mt-5 text-sm font-semibold text-td-dark">Опубликованные сообщества</h3>
          <ul className="mt-2 space-y-2">
            {published.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 rounded-xl border border-td-teal/20 bg-white p-2.5"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-td-dark">
                  {c.cover} {c.name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ id: c.id, status: "archived" })}
                >
                  Снять
                </Button>
                <DeleteCircleButton circleId={c.id} circleName={c.name} label="Удалить" />
              </li>
            ))}
          </ul>
        </>
      )}

      {deleted.length > 0 && (
        <>
          <h3 className="mt-5 text-sm font-semibold text-td-dark">Удалённые сообщества</h3>
          <p className="mt-1 text-xs text-td-muted">
            Скрыты везде. Можно восстановить — данные и участники сохранены.
          </p>
          <ul className="mt-2 space-y-2">
            {deleted.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-white p-2.5"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-td-muted">
                  {c.cover} {c.name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(c.id)}
                >
                  Восстановить
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}

      {archived.length > 0 && (
        <>
          <h3 className="mt-5 text-sm font-semibold text-td-dark">
            Снятые с публикации и отклонённые
          </h3>
          <p className="mt-1 text-xs text-td-muted">
            Обычным пользователям эти сообщества не видны.
          </p>
          <ul className="mt-2 space-y-2">
            {archived.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 rounded-xl border border-td-line bg-white p-2.5"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-td-muted">
                  {c.cover} {c.name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ id: c.id, status: "published" })}
                >
                  Вернуть
                </Button>
                <DeleteCircleButton circleId={c.id} circleName={c.name} label="Удалить" />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

type ModHost = {
  user_id: string;
  full_name: string;
  username: string | null;
  photo_url: string | null;
  city: string | null;
  created_at: string | null;
  social_links: string[] | null;
  telegram: string | null;
};

function ModerationHost({ p, userId }: { p?: ModHost; userId: string }) {
  if (!p) return null;
  const links = [
    ...(p.social_links ?? [])
      .filter((s) => s?.trim())
      .map((s) => profileHandleToUrl(s, "instagram")),
    ...(p.telegram?.trim() ? [profileHandleToUrl(p.telegram, "telegram")] : []),
  ];
  const since = p.created_at
    ? new Date(p.created_at).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  return (
    <div className="mt-3 rounded-xl bg-td-soft/60 p-2">
      <div className="flex items-center gap-2">
        {p.photo_url ? (
          <img
            loading="lazy"
            decoding="async"
            src={p.photo_url}
            alt={p.full_name}
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-xs font-semibold text-td-muted">
            {p.full_name.slice(0, 1)}
          </span>
        )}
        <div className="min-w-0">
          <Link
            to="/people/$userId"
            params={{ userId }}
            className="text-sm font-semibold text-td-dark underline-offset-2 hover:underline"
          >
            {p.full_name}
          </Link>
          {p.username && <span className="ml-1 text-xs text-td-muted">@{p.username}</span>}
          <p className="text-xs text-td-muted">
            {[p.city, since ? `в сообществе с ${since}` : null].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
      {links.length === 0 ? (
        <p className="mt-2 text-xs text-td-muted">Соцсети в профиле не указаны</p>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {links.map((u) => (
            <li key={u} className="break-all text-xs">
              <a
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-td-teal underline"
              >
                {u}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
